import { Button } from '@/app/common/components/button/Button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import SearchInput from '@/common/components/SearchInput.tsx'
import ApplicationStatusBadge from '@/common/components/StatusBadge.tsx'
import { CircleArrowsIcon } from '@/common/icons/arrows.tsx'
import { cn, currencyFormatter } from '@/common/utilities.ts'
import { formatDateToUsersTimezone } from '@/lib/utils'
import { getPaginatedTransactions } from '@/queries/transactions/queries.ts'
import { AppRoutePaths } from '@/routing/route_paths'
import { RootState } from '@/storage/store'
import { useQuery } from '@tanstack/react-query'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

const columnHelper = createColumnHelper<ITransaction>()

export type ITransaction = {
  transaction_id: number
  applicant_name: string
  student_name: string[]
  transaction_type: string
  provider_name: string
  status: number
  created_datetime: string
  invoice_quote: string
  total_amount: number
  comments: string
}

const RECORDS_PER_PAGE = 10 as const
export default function TransactionsPage() {
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RECORDS_PER_PAGE)
  const navigate = useNavigate()
  const programId = useSelector(
    (s: RootState) => s.programs.selectedProgramYear?.id
  ) as number

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['transactions', currentPage, recordsPerPage, search, programId],
    queryFn: () =>
      getPaginatedTransactions(
        programId,
        currentPage,
        recordsPerPage,
        2,
        'en',
        search
      ),
    refetchOnMount: 'always',
  })

  const transactions: ITransaction[] = data?.data || [] // Use empty array as fallback
  const totalRows = data?.total || 0
  const currentPageNumber = data?.current_page || 1

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerPageChange = (perPage: number) => {
    setRecordsPerPage(perPage)
  }

  const columns = [
    columnHelper.accessor('transaction_id', {
      id: 'transaction_id',
      header: () => <span>ID</span>,
      cell: (info) => (
        <div className="font-medium text-gray-800 ">{info.getValue()}</div>
      ),
    }),
    columnHelper.accessor('applicant_name', {
      id: 'applicant_name',
      header: () => <span>Applicant Name</span>,
      cell: (info) => <div className="">{info.getValue()}</div>,
    }),
    columnHelper.accessor('student_name', {
      id: 'student_name',
      header: () => <span>Student Name</span>,
      cell: (info) => <div className=" ">{info.getValue().join(', ')}</div>,
    }),
    columnHelper.accessor('transaction_type', {
      id: 'transaction_type',
      header: () => <span>Type</span>,
      cell: (info) => <div>{String(info.getValue())}</div>,
    }),
    columnHelper.accessor('provider_name', {
      id: 'provider',
      header: () => <span>Provider</span>,
      cell: (info) => <div>{info.getValue()}</div>,
    }),
    columnHelper.accessor('invoice_quote', {
      id: 'invoice_quote',
      header: () => 'Invoice No.',
      cell: (info) => (
        <div className="max-w-[5rem] truncate overflow-hiddden">
          {info.getValue() && `${info.getValue()}`}
        </div>
      ),
    }),
    columnHelper.accessor('total_amount', {
      id: 'total_amount',
      header: () => 'Total Amount',
      cell: (info) => <div>{currencyFormatter.format(info.getValue())}</div>,
    }),
    columnHelper.accessor('comments', {
      id: 'comments',
      header: () => 'Comments',
      cell: (info) => (
        <div className="max-w-[5rem] truncate overflow-hiddden">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => <span>Status</span>,
      cell: (info) => {
        const status = info.getValue()
        const statusText = status === (1 as number) ? 'Complete' : 'Pending'
        return <ApplicationStatusBadge status={statusText} />
      },
    }),
    columnHelper.accessor('created_datetime', {
      id: 'created_datetime',
      header: () => <span>Created At</span>,
      cell: (info) => {
        const date = info.getValue()
        return <div>{formatDateToUsersTimezone(date)}</div>
      },
    }),
    columnHelper.display({
      id: 'action',
      header: () => <span className="pr-2 2xl:p-1">Action</span>,
      cell: (info) => {
        const row = info.row.original
        return (
          <Button
            onClick={() => {
              navigate(
                AppRoutePaths.transactions.details.index.replace(
                  ':transactionId',
                  row.transaction_id.toString()
                )
              )
            }}
          >
            Review
          </Button>
        )
      },
    }),
  ]

  const table = useReactTable({
    data: transactions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(totalRows / recordsPerPage) || 1,
  })

  return (
    <>
      <section aria-labelledby="transactions-list-title">
        <Card className="mt-6">
          <CardHeader className="flex justify-between items-center text-md font-semibold">
            <div className="text-left text-lg md:text-md text-gray-900">
              Transactions
              <span className="hidden mt-1 text-sm font-normal text-gray-400 lg-sm:block lg-sm:w-full">
                {'last updated at: 06:05:32 PM'}
              </span>
            </div>
            <div>
              <div className="space-y-3 flex flex-col items-start lg-sm:flex-row lg-sm:items-center lg-sm:space-y-0 lg-sm:space-x-4">
                <SearchInput
                  id="table_search"
                  placeholder="Search Transactions"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className="flex space-x-2 lg-sm:flex-col lg-sm:space-x-0">
                  <Button
                    variant="outline"
                    className="max-w-12"
                    size="md"
                    disabled={isLoading}
                    onClick={() => refetch()}
                  >
                    <CircleArrowsIcon className="text-base" />
                  </Button>
                  <span className="block w-15ch text-sm font-normal text-gray-400 lg-sm:w-full lg-sm:hidden">
                    {'last updated at: 06:05:32 PM'}
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>
          {isLoading ? (
            <CardContent>Loading...</CardContent>
          ) : isError ? (
            <CardContent>Error fetching transactions.</CardContent>
          ) : (
            <CardContent className="flex flex-col p-0 lg:p-0 h-full w-full">
              <table>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header, index) => (
                        <th
                          key={header.id}
                          className={cn(
                            'px-2 py-3.5 text-left text-sm font-semibold text-gray-900 md:py-4 md:px-2.5',
                            index != 0 &&
                              index != headerGroup.headers.length - 1 &&
                              'hidden md:table-cell',
                            index == headerGroup.headers.length - 1 &&
                              'text-left'
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell, index) => (
                        <td
                          key={cell.id}
                          className={cn(
                            ' py-4 px-2 text-sm font-sans text-left text-gray-500 border-t border-gray-200 md:py-4 md:px-2',
                            index != 0 &&
                              index != row.getVisibleCells().length - 1 &&
                              'hidden md:table-cell',
                            index == row.getVisibleCells().length - 1 &&
                              'text-left'
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          )}
          <CardFooter className="p-0 border-t border-transparent mt-5">
            <Pagination
              itemsPerPage={recordsPerPage}
              currentPage={currentPageNumber}
              totalRows={totalRows}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </CardFooter>
        </Card>
      </section>
    </>
  )
}
